import { BadgePercent, BarChart3, Bell, Boxes, CreditCard, FileText, FolderTree, Image, LogOut, Menu, MessageSquareQuote, Package, ScrollText, Settings, ShieldCheck, ShoppingBag, Tag, Truck, Users, Webhook, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router';
import { useAuth } from '../lib/auth';
import { cx, Spinner } from './ui';

export const NAV: { to: string; label: string; perm: string; icon: typeof Package; group: string }[] = [
  { to: '/', label: 'Dashboard', perm: 'dashboard.read', icon: BarChart3, group: 'Overview' },
  { to: '/orders', label: 'Orders', perm: 'orders.read', icon: ShoppingBag, group: 'Sales' },
  { to: '/payments', label: 'Payments', perm: 'payments.read', icon: CreditCard, group: 'Sales' },
  { to: '/customers', label: 'Customers', perm: 'customers.read', icon: Users, group: 'Sales' },
  { to: '/employees', label: 'Employees', perm: 'employees.manage', icon: BadgePercent, group: 'Sales' },
  { to: '/products', label: 'Products', perm: 'products.read', icon: Package, group: 'Catalog' },
  { to: '/categories', label: 'Categories', perm: 'products.read', icon: FolderTree, group: 'Catalog' },
  { to: '/inventory', label: 'Inventory', perm: 'inventory.read', icon: Boxes, group: 'Catalog' },
  { to: '/reviews', label: 'Reviews', perm: 'reviews.moderate', icon: MessageSquareQuote, group: 'Catalog' },
  { to: '/coupons', label: 'Coupons', perm: 'coupons.manage', icon: Tag, group: 'Marketing' },
  { to: '/banners', label: 'Banners', perm: 'content.manage', icon: Image, group: 'Marketing' },
  { to: '/navigation', label: 'Navigation', perm: 'content.manage', icon: Menu, group: 'Marketing' },
  { to: '/pages', label: 'Pages', perm: 'content.manage', icon: FileText, group: 'Marketing' },
  { to: '/notifications', label: 'Notifications', perm: 'notifications.templates.manage', icon: Bell, group: 'Marketing' },
  { to: '/shipping', label: 'Shipping', perm: 'shipping.read', icon: Truck, group: 'Setup' },
  { to: '/settings', label: 'Settings', perm: 'settings.manage', icon: Settings, group: 'Setup' },
  { to: '/team', label: 'Team & roles', perm: 'users.manage', icon: ShieldCheck, group: 'Setup' },
  { to: '/audit', label: 'Audit log', perm: 'audit.read', icon: ScrollText, group: 'Setup' },
  { to: '/webhooks', label: 'Webhooks', perm: 'webhooks.manage', icon: Webhook, group: 'Setup' },
];

function Nav({ onNavigate }: { onNavigate?: () => void }) {
  const { can } = useAuth();
  const items = NAV.filter((n) => can(n.perm));
  const groups = [...new Set(items.map((i) => i.group))];
  return (
    <nav aria-label="Main" className="space-y-5">
      {groups.map((g) => (
        <div key={g}>
          <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-cream/50">{g}</p>
          {items.filter((i) => i.group === g).map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === '/'} onClick={onNavigate}
              className={({ isActive }) => cx('flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition', isActive ? 'bg-cream text-forest-900' : 'text-cream/85 hover:bg-forest-800 hover:text-cream')}>
              <Icon className="h-4 w-4" aria-hidden />{label}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="mb-6 flex items-center gap-3 px-2">
      <img src="/emblem-512.png" alt="" className="h-10 w-10 rounded-full bg-cream p-0.5" />
      <div><p className="font-heading text-lg font-semibold leading-tight text-cream">GauTattva</p><p className="text-xs text-cream/60">Admin</p></div>
    </div>
  );
}

export function Layout() {
  const { me, logout } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[248px_1fr]">
      <aside className="hidden bg-forest-900 p-4 lg:block"><div className="sticky top-4"><Brand /><Nav /></div></aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 overflow-y-auto bg-forest-900 p-4">
            <button aria-label="Close menu" className="absolute right-3 top-3 text-cream" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            <Brand /><Nav onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <div className="min-w-0">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-cream-border bg-cream/95 px-4 py-2.5 backdrop-blur">
          <button aria-label="Open menu" className="rounded p-1.5 text-forest-900 hover:bg-forest-50 lg:hidden" onClick={() => setOpen(true)}><Menu className="h-5 w-5" /></button>
          <div className="ml-auto flex items-center gap-3 text-sm">
            <span className="hidden text-ink-muted sm:inline">{me?.name ?? me?.email}</span>
            <button onClick={() => void logout()} className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 font-medium text-forest-900 hover:bg-forest-50"><LogOut className="h-4 w-4" aria-hidden />Sign out</button>
          </div>
        </header>
        <main className="mx-auto max-w-7xl p-4 sm:p-6"><Outlet /></main>
      </div>
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const loc = useLocation();
  if (status === 'loading') return <Spinner label="Signing in" />;
  if (status === 'signed-out') return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return <>{children}</>;
}

export function RequirePermission({ perm, children }: { perm: string; children: ReactNode }) {
  const { can } = useAuth();
  if (!can(perm)) return <Forbidden />;
  return <>{children}</>;
}

export function Forbidden() {
  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <h1 className="font-heading text-3xl font-semibold text-forest-900">No access</h1>
      <p className="mt-2 text-sm text-ink-muted">Your role doesn't include this section. Ask a Super Admin if you need it.</p>
    </div>
  );
}

/** First page the user can actually see (a shipping manager lands on Orders, not a 403). */
export function useHome() {
  const { can } = useAuth();
  return NAV.find((n) => can(n.perm))?.to ?? '/';
}
